import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { AuthStatus, CookieOptions, LanguageRegionMap, NewUser, User } from 'src/app/types';
import { environment } from 'src/environments/environment';
import { ClientService } from '../client/client.service';
import { LanguageService } from '../language/language.service';
import { NotificationService } from '../notification/notification.service';
import { StorageService } from '../storage/storage.service';
import { TranslateService } from '../translation/translate.service';
import { Observable } from 'rxjs';

export interface AuthError {
  code: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  
  private currentUser: CognitoUser | null = null; // Store the authenticated user
  private sessionRefreshInterval: any = null; // Store the interval ID
  public cognitoUserPool = new CognitoUserPool({
    UserPoolId: environment.awsCognitoPool,
    ClientId: environment.awsCognitoClient,
  });

  public authData = signal<AuthStatus | undefined>(undefined);
  public isLoggedIn = signal<boolean>(false);
  public user = signal<User | undefined>(undefined);
  public verification = signal<'initial' | 'email' | 'phone' | 'complete'>(
    'phone'
  );
  private isBrowser: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private storageService: StorageService,
    private clientService: ClientService,
    private router: Router,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private langService: LanguageService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.clientService.client.isLoginEnabled) {
      console.log(
        '🔴 Client has not enabled login, not initializing auth service'
      );
      return;
    }

    if (this.getAccessToken()) {
      this.isLoggedIn.set(true);
    }

    this.checkVerification();
  }

  public checkVerification() {
    const user = this.storageService.get('user');

    if (user) {
      const userData = {
        Username: JSON.parse(user).email,
        Pool: this.cognitoUserPool,
      };
      this.currentUser = new CognitoUser(userData);

      const _user = JSON.parse(user) as User;
      this.user.set(_user);
      if (_user.emailVerified && _user.phoneNumberVerified) {
        this.verification.set('complete');
      } else if (!_user.emailVerified) {
        this.verification.set('email');
      } else {
        this.verification.set('phone');
      }
    } else {
      this.verification.set('email');
    }
  }

  public signUp = async (newUser: NewUser): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      // Create user attributes array
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({
          Name: 'phone_number',
          Value: newUser.phone_number,
        }),
      ];

      this.cognitoUserPool.signUp(
        newUser.email,
        newUser.password,
        attributeList, // Pass attributes here
        [],
        (err, data) => {
          if (err) {
            reject({
              // @ts-ignore
              code: err?.code ?? 'UnknownError',
              message: err?.message ?? 'An unknown error occurred',
            } as AuthError);
            return;
          }

          resolve(data?.user.getUsername()); // Resolve when signUp succeeds
        }
      );
    });
  };

  public confirmUser = async (
    email: string,
    verificationCode: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          reject({
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'An unknown error occurred',
          });
          return;
        }
        resolve();
      });
    });
  };

  public resendVerificationCode = async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject({
            // @ts-ignore
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'An unknown error occurred',
          });
          return;
        }
        console.log('Verification code resent successfully:', result);
        resolve();
      });
    });
  };

  public requestPhoneVerificationCode = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        // TODO: log user out, snackbar error notification
        return;
      }

      this.currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject({
            code: err?.code ?? 'UnknownError',
            message:
              err?.message ?? 'An error occurred while getting the session.',
          });
          return;
        }
        // @ts-ignore
        this.currentUser.getAttributeVerificationCode('phone_number', {
          onSuccess: () => {
            console.log('✅ Phone verification code sent successfully.');
            resolve();
          },
          onFailure: (error) => {
            reject({
              // @ts-ignore
              code: error?.code ?? 'UnknownError',
              message:
                error?.message ?? 'Failed to send phone verification code.',
            });
          },
        });
      });
    });
  };

  public confirmPhoneVerificationCode = async (
    verificationCode: string
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        this.isLoggedIn.set(false);
        this.clearTokens();
        return;
      }

      // ✅ Try to refresh session before verifying phone
      try {
        await this.refreshSession();
      } catch (error) {
        reject(error);
        return;
      }

      this.currentUser.verifyAttribute('phone_number', verificationCode, {
        onSuccess: () => {
          console.log('✅ Phone number verified successfully.');

          // ✅ Update stored user data
          const storedUser = this.storageService.get('user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            user.phoneNumberVerified = true;
            this.storageService.set('user', JSON.stringify(user));
            this.user.set(user);
          }

          this.verification.set('complete');
          resolve();
        },
        onFailure: (error) => {
          reject({
            //@ts-ignore
            code: error?.code ?? 'UnknownError',
            message: error?.message ?? 'Phone verification failed.',
          });
        },
      });
    });
  };

  public signIn = async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          console.log('✅ Login successful:', session);

          this.currentUser = cognitoUser; // ✅ Store the authenticated user instance

          // ✅ Start the session refresh interval
          this.startSessionRefresh();

          // ✅ Store user data
          const payload = session.getIdToken().payload;
          const emailVerified: boolean = payload?.['email_verified'];
          const phoneNumberVerified: boolean =
            payload?.['phone_number_verified'];

          const user: User = {
            sub: payload?.['sub'],
            email: payload?.['email'],
            emailVerified,
            phoneNumber: payload?.['phone_number'],
            phoneNumberVerified,
          };

          this.storageService.set('user', JSON.stringify(user));

          this.user.set(user);

          if (emailVerified && phoneNumberVerified) {
            this.verification.set('complete');
          } else if (!emailVerified) {
            this.verification.set('email');
          } else {
            this.verification.set('phone');
          }

          // ✅ Extract and store tokens
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          this.saveTokens(idToken, accessToken, refreshToken);
          this.isLoggedIn.set(true);

          resolve();
        },
        onFailure: (err) => {
          reject({
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'Login failed.',
          });
        },
      });
    });
  };

  public startSessionRefresh(): void {
    // ✅ Prevent multiple intervals
    if (this.sessionRefreshInterval) {
      clearInterval(this.sessionRefreshInterval);
    }

    if (!this.isBrowser) {
      console.log('not starting interval');
      return;
    }
    this.sessionRefreshInterval = setInterval(() => {
      if (this.isLoggedIn()) {
        this.refreshSession().catch((err) =>
          console.error('🔴 Session refresh failed:', err)
        );
      }
    }, 50 * 60 * 1000); // Refresh every 50 minutes

    console.log('✅ started refresh token intervals');
  }

  public saveTokens(
    idToken: string,
    accessToken: string,
    refreshToken: string
  ): void {
    const cookieOptions: CookieOptions = {
      expires: 1, // Expires in 1 day
      path: '/',
      secure: true, // Ensures HTTPS only
      sameSite: 'Strict', // Protects against CSRF
      partitioned: false, // Not needed in most cases
    };

    this.storageService.set('id_token', idToken, cookieOptions);
    this.storageService.set('access_token', accessToken, cookieOptions);
    this.storageService.set('refresh_token', refreshToken, {
      ...cookieOptions,
      expires: 30, // Refresh token lasts longer (30 days)
    });

    console.log('✅ Tokens saved securely in cookies.');
  }

  public getAccessToken(): string | null {
    return this.storageService.get('access_token') ?? null;
  }

  public getIdToken(): string | null {
    return this.storageService.get('id_token') ?? null;
  }
  public getRefreshToken(): string | null {
    return this.storageService.get('refresh_token') ?? null;
  }

  public clearTokens(): void {
    this.storageService.delete('id_token', '/');
    this.storageService.delete('access_token', '/');
    this.storageService.delete('refresh_token', '/');
    this.storageService.delete('user', '/');
    this.storageService.delete('authStatus', '/');
  }

  public signOut(): void {
    this.clearTokens();
    this.isLoggedIn.set(false);
    this.user.set(undefined);
    this.currentUser = null;
    this.verification.set('initial');
    this.authData.set(undefined);

    // ✅ Stop session refresh
    if (this.sessionRefreshInterval) {
      clearInterval(this.sessionRefreshInterval);
      this.sessionRefreshInterval = null;
    }

    console.log('🔴 User logged out, tokens cleared.');
  }

  public refreshSession = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        return;
      }

      const refreshToken = new CognitoRefreshToken({
        RefreshToken: this.getRefreshToken() ?? '',
      });

      // ✅ Refresh the session using the Refresh Token
      // @ts-ignore
      this.currentUser.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          console.error('Session refresh failed:', err);
          this.isLoggedIn.set(false);
          this.clearTokens();
          reject({
            code: err?.code ?? 'RefreshFailed',
            message:
              err?.message ?? 'Session refresh failed. Please log in again.',
          });
          this.notificationService.showCustomMessage(
            '',
            'Session expired, please sign back in.'
          );
          this.router.navigateByUrl(`/${this.langService.lang()}/login`);
          return;
        }

        console.log('✅ Session refreshed:', newSession);

        // ✅ Store new tokens
        const idToken = newSession.getIdToken().getJwtToken();
        const accessToken = newSession.getAccessToken().getJwtToken();
        const refreshToken = newSession.getRefreshToken().getToken();

        this.saveTokens(idToken, accessToken, refreshToken);
        this.reloadAuthData();
        this.isLoggedIn.set(true);
        resolve();
      });
    });
  };

  public isTokenOlderThanMinutes(token: string, minutes: number): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const iat = payload.iat; // Issued at timestamp (seconds)
      if (!iat) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime - iat > minutes * 60; // Check if token is older than X minutes
    } catch (error) {
      console.log('🔴 Failed to decode token:', error);
      return true;
    }
  }
  
  public async reloadAuthData(callback?: Function) {
    const currentLang = this.translate.language;
    try {
      const token = this.storageService.get('access_token');
      if (!this.authData()) {
        // ✅ Waits for `authStatus` to return data
        const data = await this.clientService.authStatus({
          headers: {
            'Accept-Language': currentLang,
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        this.authData.set(data);
        if (data) {
          console.log('✅ Setting auth status:', data.userStatus);
          this.storageService.set('authStatus', data.userStatus);
        } else {
          console.log('🔴 No auth status returned');
        }
      }
      this.verification.set(this.verification());
      
    } catch (error) {
      console.log('🔴 Error fetching auth status on sign in', error);
    }
    
  }

  public verifyProcessPending() {
    const signedIn = this.isLoggedIn();
    if (signedIn) {
      return false;
    }
    const verificationStatus = this.verification();
    const notVerified = 'phone|email'.indexOf(verificationStatus) !== -1;
    const data = this.authData();
    let pending = false;

    if (signedIn) {
      if (data?.hasAccess) {
        return false
      }
      if (notVerified) {
        pending = true;
      }
      if (!data?.hasCompletedSignup || !data?.hasRequestedAccessToClient) {
        pending = true;
      }
      if (data?.userStatus === 'PENDING' || data?.userStatus === 'REJECTED' ) {
        pending = true;
      }
    }
    return pending;
  }

  public async initialize() {
    return new Promise(async (resolve, reject) => {
      if (this.isBrowser) {
        const idToken = this.getIdToken();
        if (idToken) {
          if (this.isTokenOlderThanMinutes(idToken, 5)) {
            console.log(
              '🔄 ID Token is older than 5 minutes, attempting refresh...'
            );
            try {
              await this.refreshSession();
              console.log('✅ Token successfully refreshed on page load');
              this.isLoggedIn.set(true);
              this.startSessionRefresh();
            } catch (error:any){
              console.error('🔴 Token refresh failed:', error);
            };
            
          } else {
            console.log('✅ Session is valid on reload');
            this.isLoggedIn.set(true);
            this.startSessionRefresh();
          }

          await this.reloadAuthData();

        }
      }
      resolve(true);
    });
  }

}
